import React, { useState, useEffect, useMemo } from 'react';
import api from '../../config/api';
import { COUNTRIES } from '../../utils/countriesAndProvinces';

const IDENTITY_TYPES = ["Carte d'identité", "Carta d'Identità", 'Passeport', 'Passaporto'];

const SEASONS = [
  { value: '2025-2026', label: '2025-2026' },
  { value: '2024-2025', label: '2024-2025' },
  { value: '', label: '— Aucune (legacy) —' },
];

function calcUnder18(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age < 18;
}

const emptyMembershipFlags = () => ({
  star: false,
  starAboClub: false,
  base: false,
  plus: false,
  abonnePlus: false,
  plusU14: false,
  standard: false,
  abonneStandard: false,
  abonnes: false,
  nonAbonne: false,
  newsletter: false,
  under5: false,
  jofcAdult: false,
  jofcUnder14: false,
  jofcUnder5: false,
  jofcAbonnesFull: false,
});

const emptyForm = () => ({
  email: '',
  civilite: 'Monsieur',
  firstName: '',
  lastName: '',
  sexe: 'Homme',
  dateOfBirth: '',
  phone: '',
  preferredLanguage: 'fr',
  season: '2025-2026',
  isMember: false,
  membershipFlags: emptyMembershipFlags(),
  hasTessera: false,
  tesseraNumber: '',
  tesseraExpiry: '',
  tesseraPIN: '',
  birthCountry: 'Suisse',
  birthCity: '',
  birthCanton: '',
  street: '',
  streetNumber: '',
  city: '',
  canton: '',
  postalCode: '',
  country: 'Suisse',
  identityType: "Carte d'identité",
  identityNumber: '',
  identityExpiry: '',
  identityIssueDate: '',
  identityIssueCanton: '',
  identityIssueCity: '',
  codiceFiscale: '',
  termsAccepted: false,
  parentEmail: '',
  parentPhone: '',
  parentFirstName: '',
  parentLastName: '',
  parentSexe: 'Homme',
});

/**
 * Modal — création utilisateur via POST /users/register-member.
 * Mot de passe généré côté serveur si non fourni.
 */
export default function AdminAddUserModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(emptyForm());
      setErrors([]);
    }
  }, [isOpen]);

  const isMinor = useMemo(() => calcUnder18(formData.dateOfBirth), [formData.dateOfBirth]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('membershipFlags.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        membershipFlags: {
          ...prev.membershipFlags,
          [field]: checked,
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    const next = [];
    if (!formData.email.trim()) next.push('Email obligatoire');
    if (!formData.firstName.trim()) next.push('Prénom obligatoire');
    if (!formData.lastName.trim()) next.push('Nom obligatoire');
    if (!formData.dateOfBirth) next.push('Date de naissance obligatoire');
    if (!formData.phone.trim()) next.push('Téléphone obligatoire');
    if (!formData.birthCountry || !formData.birthCity.trim()) {
      next.push('Pays et ville de naissance obligatoires');
    }
    if (!formData.street.trim() || !formData.city.trim()) {
      next.push('Adresse : rue et ville obligatoires');
    }
    if (!formData.postalCode.trim()) next.push('Code postal obligatoire');
    if (
      !formData.identityNumber.trim() ||
      !formData.identityExpiry ||
      !formData.identityIssueDate ||
      !formData.identityIssueCanton.trim() ||
      !formData.identityIssueCity.trim()
    ) {
      next.push(
        "Pièce d'identité : numéro, échéance, date et lieu d'émission (canton/département, commune) obligatoires"
      );
    }
    if (formData.country === 'Italie' && !formData.codiceFiscale.trim()) {
      next.push('Codice fiscale obligatoire pour la résidence en Italie');
    }
    if (!formData.termsAccepted) next.push('Les conditions générales doivent être acceptées');

    if (formData.hasTessera) {
      if (!formData.tesseraNumber.trim()) next.push('Numéro Juventus Card obligatoire');
      if (!formData.tesseraExpiry) next.push('Échéance Juventus Card obligatoire');
      if (!formData.tesseraPIN.trim()) next.push('PIN Webticket obligatoire');
    }

    if (isMinor) {
      if (!formData.parentEmail.trim()) next.push('Email du parent obligatoire (mineur)');
      if (!formData.parentPhone.trim()) next.push('Téléphone du parent obligatoire (mineur)');
      if (!formData.parentFirstName.trim() || !formData.parentLastName.trim()) {
        next.push('Prénom et nom du parent obligatoires (mineur)');
      }
    }

    setErrors(next);
    return next.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const juventusFanCard = formData.hasTessera
      ? {
          has: true,
          number: formData.tesseraNumber.trim(),
          expiryDate: formData.tesseraExpiry,
          webticketPIN: formData.tesseraPIN.trim(),
        }
      : { has: false };

    const payload = {
      email: formData.email.trim().toLowerCase(),
      civilite: formData.civilite,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      sexe: formData.sexe,
      dateOfBirth: formData.dateOfBirth,
      phone: formData.phone.trim(),
      preferredLanguage: formData.preferredLanguage,
      birthPlace: {
        country: formData.birthCountry,
        city: formData.birthCity.trim(),
        ...(formData.birthCanton.trim() ? { canton: formData.birthCanton.trim() } : {}),
      },
      address: {
        street: formData.street.trim(),
        ...(formData.streetNumber.trim() ? { streetNumber: formData.streetNumber.trim() } : {}),
        city: formData.city.trim(),
        ...(formData.canton.trim() ? { canton: formData.canton.trim() } : {}),
        postalCode: formData.postalCode.trim(),
        country: formData.country,
      },
      identity: {
        type: formData.identityType,
        number: formData.identityNumber.trim(),
        expiryDate: formData.identityExpiry,
        issueDate: formData.identityIssueDate,
        issueCanton: formData.identityIssueCanton.trim(),
        issueCity: formData.identityIssueCity.trim(),
      },
      consents: {
        marketing: false,
        profiling: false,
        minorAuthorization: isMinor,
        imageRights: false,
        termsAccepted: true,
      },
      juventusFanCard,
      subscription: { has: false },
      blackWhiteCard: { has: false },
      otherFanClub: { isMember: false },
      membershipFlags: { ...formData.membershipFlags },
      isMember: formData.isMember,
    };

    if (formData.season) {
      payload.season = formData.season;
    }

    if (formData.country === 'Italie' && formData.codiceFiscale.trim()) {
      payload.codiceFiscale = formData.codiceFiscale.trim();
    }

    if (isMinor) {
      payload.isMinor = 'true';
      payload.parentInfo = {
        email: formData.parentEmail.trim(),
        phone: formData.parentPhone.trim(),
        firstName: formData.parentFirstName.trim(),
        lastName: formData.parentLastName.trim(),
        sexe: formData.parentSexe,
      };
    }

    setSubmitting(true);
    setErrors([]);
    try {
      await api.post('/users/register-member', payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      const reqUrl = [err.config?.baseURL, err.config?.url]
        .filter(Boolean)
        .join('')
        .replace(/([^:])\/{2,}/g, '$1/');
      const list = [];

      if (status === 404) {
        list.push(
          `404 — API introuvable : ${reqUrl || '(URL inconnue)'}. Contrôlez : (1) nginx / proxy envoie /api vers Node, (2) processus API redémarré avec la dernière version, (3) si le site est dans un sous-chemin, rebuild avec « homepage » ou PUBLIC_URL pour que l’API soit sous la même base.`
        );
      } else if (data?.errors && Array.isArray(data.errors)) {
        for (const row of data.errors) {
          list.push(row.msg || row.message || JSON.stringify(row));
        }
      } else if (data?.message) {
        list.push(String(data.message));
      } else if (data?.error) {
        list.push(typeof data.error === 'string' ? data.error : 'Erreur serveur');
      } else {
        list.push(err.message || 'Erreur réseau');
      }
      setErrors(list);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="modal-content modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="modal-header">
          <h2>Ajouter un utilisateur</h2>
          <button type="button" className="btn-close" disabled={submitting} onClick={onClose}>
            ×
          </button>
        </div>

        <p style={{ padding: '0 1.5rem', marginTop: 0, color: '#555', fontSize: '0.95rem' }}>
          Création d’un compte (admin), sans pièces jointes.{' '}
          <strong>
            Aucun mot de passe n’est demandé : un mot de passe aléatoire est généré côté serveur.
          </strong>{' '}
          Le membre pourra utiliser « Mot de passe oublié » sur la page de connexion.
          {isMinor && (
            <strong style={{ display: 'block', marginTop: '0.5rem', color: '#c0392b' }}>
              Mineur : renseignez les informations du représentant légal.
            </strong>
          )}
        </p>

        {errors.length > 0 && (
          <div
            className="admin-register-errors"
            style={{ margin: '0 1.5rem 1rem' }}
          >
            <strong>Veuillez corriger :</strong>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
              {errors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="admin-form" style={{ padding: '0 1.5rem 1.5rem' }}>
          <div className="detail-section">
            <h3>Identifiant</h3>
            <div className="form-group">
              <label>Email *</label>
              <input name="email" type="email" value={formData.email} onChange={handleChange} required autoComplete="off" />
            </div>
          </div>

          <div className="detail-section">
            <h3>Statut membre</h3>
            <div className="form-group checkbox">
              <label>
                <input name="isMember" type="checkbox" checked={formData.isMember} onChange={handleChange} />
                Membre actif (sinon compte inactif / inscription à valider selon votre processus)
              </label>
            </div>
          </div>

          <div className="detail-section">
            <h3>Type d&apos;adhésion</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
              }}
            >
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.jofcAdult"
                    checked={formData.membershipFlags.jofcAdult}
                    onChange={handleChange}
                  />
                  JOFC Adult
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.jofcUnder14"
                    checked={formData.membershipFlags.jofcUnder14}
                    onChange={handleChange}
                  />
                  JOFC under 14
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.jofcUnder5"
                    checked={formData.membershipFlags.jofcUnder5}
                    onChange={handleChange}
                  />
                  JOFC Under 5
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.star"
                    checked={formData.membershipFlags.star}
                    onChange={handleChange}
                  />
                  Abonné STAR
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.starAboClub"
                    checked={formData.membershipFlags.starAboClub}
                    onChange={handleChange}
                  />
                  Abonné STAR abo club
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.base"
                    checked={formData.membershipFlags.base}
                    onChange={handleChange}
                  />
                  Abonné BASE
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.abonnes"
                    checked={formData.membershipFlags.abonnes}
                    onChange={handleChange}
                  />
                  Abonné FULL
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.abonnePlus"
                    checked={formData.membershipFlags.abonnePlus}
                    onChange={handleChange}
                  />
                  Abonné membre PLUS
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.abonneStandard"
                    checked={formData.membershipFlags.abonneStandard}
                    onChange={handleChange}
                  />
                  Abonné membre STANDARD
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.plus"
                    checked={formData.membershipFlags.plus}
                    onChange={handleChange}
                  />
                  Membre PLUS
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.standard"
                    checked={formData.membershipFlags.standard}
                    onChange={handleChange}
                  />
                  Membre STANDARD
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.plusU14"
                    checked={formData.membershipFlags.plusU14}
                    onChange={handleChange}
                  />
                  Membre UNDER14
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.under5"
                    checked={formData.membershipFlags.under5}
                    onChange={handleChange}
                  />
                  Membre UNDER5
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.nonAbonne"
                    checked={formData.membershipFlags.nonAbonne}
                    onChange={handleChange}
                  />
                  Non Abonné
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.newsletter"
                    checked={formData.membershipFlags.newsletter}
                    onChange={handleChange}
                  />
                  Newsletter
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="membershipFlags.jofcAbonnesFull"
                    checked={formData.membershipFlags.jofcAbonnesFull}
                    onChange={handleChange}
                  />
                  JOFC Abonné FULL
                </label>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Identité</h3>
            <div className="form-group">
              <label>Civilité *</label>
              <select name="civilite" value={formData.civilite} onChange={handleChange} required>
                <option value="Monsieur">Monsieur</option>
                <option value="Madame">Madame</option>
                <option value="M">M</option>
                <option value="Mme">Mme</option>
              </select>
            </div>
            <div className="form-group">
              <label>Prénom *</label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Sexe *</label>
              <select name="sexe" value={formData.sexe} onChange={handleChange} required>
                <option value="Homme">Homme</option>
                <option value="Femme">Femme</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date de naissance *</label>
              <input name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Téléphone *</label>
              <input name="phone" value={formData.phone} onChange={handleChange} placeholder="+41…" required />
            </div>
            <div className="form-group">
              <label>Langue</label>
              <select name="preferredLanguage" value={formData.preferredLanguage} onChange={handleChange}>
                <option value="fr">fr</option>
                <option value="it">it</option>
              </select>
            </div>
            <div className="form-group">
              <label>Saison</label>
              <select name="season" value={formData.season} onChange={handleChange}>
                {SEASONS.map((s) => (
                  <option key={s.value || 'none'} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="detail-section">
            <h3>Lieu de naissance</h3>
            <div className="form-group">
              <label>Pays *</label>
              <select name="birthCountry" value={formData.birthCountry} onChange={handleChange} required>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Ville *</label>
              <input name="birthCity" value={formData.birthCity} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Canton / province</label>
              <input name="birthCanton" value={formData.birthCanton} onChange={handleChange} />
            </div>
          </div>

          <div className="detail-section">
            <h3>Adresse</h3>
            <div className="form-group">
              <label>Rue *</label>
              <input name="street" value={formData.street} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Numéro</label>
              <input name="streetNumber" value={formData.streetNumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Code postal *</label>
              <input name="postalCode" value={formData.postalCode} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Ville *</label>
              <input name="city" value={formData.city} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Canton</label>
              <input name="canton" value={formData.canton} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Pays de résidence *</label>
              <select name="country" value={formData.country} onChange={handleChange} required>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            {formData.country === 'Italie' && (
              <div className="form-group">
                <label>Codice fiscale *</label>
                <input name="codiceFiscale" value={formData.codiceFiscale} onChange={handleChange} required />
              </div>
            )}
          </div>

          <div className="detail-section">
            <h3>Pièce d&apos;identité</h3>
            <div className="form-group">
              <label>Type *</label>
              <select name="identityType" value={formData.identityType} onChange={handleChange} required>
                {IDENTITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Numéro *</label>
              <input name="identityNumber" value={formData.identityNumber} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Date d&apos;expiration *</label>
              <input name="identityExpiry" type="date" value={formData.identityExpiry} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Date d&apos;émission *</label>
              <input name="identityIssueDate" type="date" value={formData.identityIssueDate} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Canton/Département d&apos;émission *</label>
              <input name="identityIssueCanton" value={formData.identityIssueCanton} onChange={handleChange} required placeholder="VD, VS, GE, 75, MI…" />
            </div>
            <div className="form-group">
              <label>Commune/localité d&apos;émission *</label>
              <input name="identityIssueCity" value={formData.identityIssueCity} onChange={handleChange} required placeholder="Exemple : Lausanne" />
            </div>
          </div>

          <div className="detail-section">
            <h3>Juventus Card</h3>
            <div className="form-group checkbox">
              <label>
                <input name="hasTessera" type="checkbox" checked={formData.hasTessera} onChange={handleChange} />
                Possède une Juventus Card (renseigner les champs ci-dessous)
              </label>
            </div>
            {formData.hasTessera && (
              <>
                <div className="form-group">
                  <label>Numéro Juventus Card *</label>
                  <input
                    type="text"
                    name="tesseraNumber"
                    maxLength={12}
                    autoComplete="off"
                    inputMode="numeric"
                    value={formData.tesseraNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Échéance *</label>
                  <input name="tesseraExpiry" type="date" value={formData.tesseraExpiry} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>PIN Webticket *</label>
                  <input name="tesseraPIN" value={formData.tesseraPIN} onChange={handleChange} autoComplete="off" />
                </div>
              </>
            )}
          </div>

          {isMinor && (
            <div className="detail-section">
              <h3>Représentant légal (mineur)</h3>
              <div className="form-group">
                <label>Email du parent / tuteur *</label>
                <input name="parentEmail" type="email" value={formData.parentEmail} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Téléphone du parent *</label>
                <input name="parentPhone" value={formData.parentPhone} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Prénom *</label>
                <input name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input name="parentLastName" value={formData.parentLastName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Sexe</label>
                <select name="parentSexe" value={formData.parentSexe} onChange={handleChange}>
                  <option value="Homme">Homme</option>
                  <option value="Femme">Femme</option>
                </select>
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="form-group checkbox">
              <label>
                <input name="termsAccepted" type="checkbox" checked={formData.termsAccepted} onChange={handleChange} />
                CGU / conditions générales acceptées *
              </label>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? 'Création…' : 'Créer l’utilisateur'}
            </button>
            <button type="button" className="btn-cancel" disabled={submitting} onClick={onClose}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
